import { useMemo } from 'react';
import { isValid } from 'date-fns';
import { Button, Stack } from '@rossum/ui/material';
import {
  DateRange,
  DateRangeCalendar,
  dateRangeCalendarClasses,
  dateRangePickerDayClasses,
} from '@rossum/ui/x-date-pickers-pro';
import { AdapterDateFns } from '@rossum/ui/x-date-pickers-pro/AdapterDateFns';
import { useIntl } from '../ComponentsContext';
import { DATE_RANGE_CALENDAR_WIDTH, DateTimeInput } from './DateTimeInput';
import { handleNonRangedDateChange } from './handleNonRangedDateChange';
import {
  getCustomDateRangeTime,
  getFixedDateShortcuts,
  getIsActive,
  rangedDateShortcuts,
} from './shortcuts';
import { applyTextButtonStyle, shortcutStyles } from './styles';
import { DateOperator, SelectedDates } from './types';

const { formatTokenMap } = new AdapterDateFns();

type DateTimePickerProps = {
  selectedDates: SelectedDates;
  setSelectedDates: (dateRange: SelectedDates) => void;
  operatorValue: DateOperator;
  hideTimePickers?: boolean;
  locale: string;
};

export const DateTimeRangePicker = ({
  selectedDates,
  setSelectedDates,
  operatorValue,
  hideTimePickers,
  locale,
}: DateTimePickerProps) => {
  const intl = useIntl();

  const isRangedOperator = operatorValue === 'is';

  const handleDatePickerChange = (
    dateRange: DateRange<Date>,
    selectionState?: 'partial' | 'finish' | 'shallow'
  ) => {
    const [from, to] = dateRange;

    if (!isRangedOperator) {
      const targetDate = handleNonRangedDateChange({
        dateRange,
        selectionState,
        selectedDates,
        operatorValue,
      });
      return setSelectedDates([targetDate, targetDate]);
    }

    if (!from || !to) return setSelectedDates(dateRange);

    return setSelectedDates(
      getCustomDateRangeTime([from, to], isRangedOperator)
    );
  };

  const shortcuts = useMemo(() => {
    const fixedShortcuts = getFixedDateShortcuts(operatorValue);

    return [...fixedShortcuts, ...rangedDateShortcuts];
  }, [operatorValue]);

  return (
    <Stack direction="row">
      <Stack
        my={1}
        pr={4}
        minWidth={150}
        sx={{
          borderRight: theme => `1px solid ${theme.palette.other.muted}`,
        }}
      >
        {shortcuts.map(shortcut => (
          <Button
            key={shortcut.labelKey}
            onClick={() => {
              setSelectedDates(shortcut.getValue());
            }}
            variant="text"
            sx={{
              ...shortcutStyles,
              ...applyTextButtonStyle(
                getIsActive(selectedDates, shortcut.getValue())
              ),
            }}
          >
            {intl.formatMessage({
              id: `containers.filtering.shortcuts.${shortcut.labelKey}`,
            })}
          </Button>
        ))}
      </Stack>
      <Stack>
        <DateRangeCalendar
          value={selectedDates}
          onChange={handleDatePickerChange}
          sx={{
            [`& .${dateRangePickerDayClasses.rangeIntervalPreview}`]:
              isRangedOperator
                ? {}
                : {
                    border: 'none',
                    // to account for border-width
                    margin: '2px',
                  },
            [`& .${dateRangeCalendarClasses.monthContainer}:not(:last-of-type)`]:
              {
                borderRight: 'none',
              },
          }}
        />

        <Stack direction="row" maxWidth={DATE_RANGE_CALENDAR_WIDTH}>
          <DateTimeInput
            date-cy="date-time-range-picker-start"
            value={selectedDates[0]}
            isActive={!selectedDates[0]}
            onChange={value => {
              if (value && isValid(value)) {
                setSelectedDates([value, selectedDates[1]]);
              }
            }}
            formatTokenMap={formatTokenMap}
            hideTime={hideTimePickers}
            locale={locale}
          />

          {isRangedOperator ? (
            <DateTimeInput
              data-cy="date-time-range-picker-end"
              value={selectedDates[1]}
              isActive={
                !!(isRangedOperator && selectedDates[0] && !selectedDates[1])
              }
              onChange={value => {
                if (value && isValid(value)) {
                  setSelectedDates([selectedDates[0], value]);
                }
              }}
              formatTokenMap={formatTokenMap}
              hideTime={hideTimePickers}
              locale={locale}
            />
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );
};
